import { EXTERNAL_USER_KEY } from '../constants';
import { USER_ROLE_ENUMS, USER_ROLE_VIEWER } from '../constants/video';
import { REACT_APP_WATCH_SHARED_DATA } from '@/modules/common/constants/globals';

import { toValidEnum } from './config';

type ContentOwnersType = {
  contentOwnerId: number;
  publisherOwnsContent: boolean;
  name: string;
  status: number;
  isPublic: boolean;
};

type RawUser = {
  id?: number | string;
  accountId?: number | string;
  firstName?: string;
  lastName?: string;
  email?: string;
  permissions?: string[];
  role?: string;
  contentOwners?: ContentOwnersType[];
};

type FormattedUser = {
  id: number | string;
  accountId?: number;
  firstName: string;
  lastName: string;
  email: string;
  permissions: string[];
  contentOwners: ContentOwnersType[];
};

const getUserFullNameProps = (rawUser: Partial<RawUser>): { firstName: string; lastName: string } => {
  let { firstName } = rawUser;
  let { lastName } = rawUser;

  if (!firstName && !lastName) {
    firstName = 'Anyclip';
    lastName = 'Services';
  } else if (!firstName) {
    firstName = lastName || '';
  } else if (!lastName) {
    lastName = firstName;
  }

  return {
    firstName: firstName || '',
    lastName: lastName || '',
  };
};

export const getUser = (): FormattedUser => {
  let user: Partial<RawUser> = {};

  try {
    const userStorage = window[REACT_APP_WATCH_SHARED_DATA][EXTERNAL_USER_KEY];

    user = {
      ...userStorage,
      ...getUserFullNameProps(userStorage),
    };
  } catch {
    /* empty */
  }

  let accountId;

  if (user.accountId) {
    accountId = +user.accountId;
  }

  return {
    accountId,
    id: user.id ? `${user.id}` : 0,
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    email: user.email ?? '',
    permissions: user.permissions ?? [],
    contentOwners: (user.contentOwners as ContentOwnersType[]) ?? [],
  };
};

export const getFormattedUser = (rawUser: RawUser): FormattedUser => {
  const { lastName, firstName } = getUserFullNameProps(rawUser);

  let accountId;

  if (rawUser.accountId) {
    accountId = +rawUser.accountId;
  }

  return {
    ...rawUser,
    id: rawUser.id || 0,
    accountId,
    email: rawUser.email || '',
    firstName,
    lastName,
    permissions: rawUser.permissions || [],
    contentOwners: rawUser.contentOwners || [],
  };
};

export const getFormattedSharedUser = (rawUser: RawUser): FormattedUser & { role: string } => {
  const { lastName, firstName } = getUserFullNameProps(rawUser);

  let accountId;

  if (rawUser.accountId) {
    accountId = +rawUser.accountId;
  }

  return {
    ...rawUser,
    id: rawUser.id || 0,
    accountId,
    email: rawUser.email || '',
    firstName,
    lastName,
    permissions: rawUser.permissions || [],
    contentOwners: rawUser.contentOwners || [],
    role: toValidEnum(rawUser.role, USER_ROLE_ENUMS, USER_ROLE_VIEWER),
  };
};

export const hasPermission = (permission: string): boolean => getUser().permissions.includes(permission);
